import React, { useEffect, useState } from "react";
import {
  darkNoTransaction,
  noTransaction,
  pendingClock,
} from "../../assets/images";
import nairaLogo from "../../assets/images/Naira.svg";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import ClipLoader from "react-spinners/ClipLoader";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { IoClose } from "react-icons/io5";
import { API } from "../../constants/api";
import { useUser } from "../../context/user-context";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import { formatDateWithSuffix, formatAmount } from "../../utils/formatTime";
import TransactionHistoryRefreshButton from "../../components/TransactionHistoryRefreshButton";
import {
  getTransactionStatusClass,
  getTransactionStatusLabel,
} from "../../utils/transactionStatus";

const TXN_LIMIT = 10;

type NairaTransaction = {
  _id: string;
  type: string;
  amount: number;
  fee: number;
  narration: string;
  status: string;
  createdAt: string;
  counterParty?: {
    accountName?: string;
    accountNumber?: string;
    bankName?: string;
  };
};

const formatTxnTypeLabel = (type: string) => {
  if (type === "WITHDRAWAL") return "Withdrawal";
  if (type === "DEPOSIT") return "Deposit";
  if (type === "BILL_PAYMENT") return "Bill payment";
  return type.charAt(0) + type.slice(1).toLowerCase().replace(/_/g, " ");
};

// Shape the status helpers expect. Bill payments count as utilities so a FAILED
// one shows as failed; failed withdrawals stay masked as pending (product rule
// in utils/transactionStatus.ts).
const toStatusInput = (transaction: NairaTransaction) => ({
  status: transaction.status,
  utility: transaction.type === "BILL_PAYMENT",
});

const NairaRecentTnxn = () => {
  const { theme } = useUser();
  const axiosInstance = useAuthAxios();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [transactionsCount, setTransactionsCount] = useState(0);
  const [selectedTransaction, setSelectedTransaction] =
    useState<NairaTransaction | null>(null);

  const fetchTransactions = async () => {
    const response = await axiosInstance.get(
      API.getNairaWalletTransactions(page, TXN_LIMIT)
    );
    return response.data;
  };

  const {
    data: activity,
    error,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["naira-wallet-transactions", page],
    queryFn: fetchTransactions,
    retry: 1,
  });

  const transactions: NairaTransaction[] = activity?.data?.transactions ?? [];
  const pagination = activity?.data?.pagination;

  useEffect(() => {
    if (transactions.length > 0) {
      const pages = pagination?.pages ?? 1;
      setTotalPages(pages);
      setHasMore((pagination?.page ?? page) < pages);
      setTransactionsCount(transactions.length);
    } else {
      setHasMore(false);
      setTransactionsCount(0);
      if (pagination?.pages) {
        setTotalPages(pagination.pages);
      }
    }
  }, [
    activity,
    page,
    pagination?.page,
    pagination?.pages,
    transactions.length,
  ]);

  useEffect(() => {
    if (error) {
      const err = error as { message?: string; data?: { message?: string } };
      toast.error(errorMessage(err?.message || err?.data?.message));
    }
  }, [error]);

  const handleRefresh = () => {
    if (page !== 1) {
      setPage(1);
    } else {
      refetch();
    }
  };

  const handleNextPage = () => {
    if (hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1 && !isLoading) {
      setPage((prev) => prev - 1);
    }
  };

  return (
    <div className="mb-8 mt-8 flex h-[500px] w-full flex-col overflow-hidden rounded-xl bg-white font-manrope dark:bg-[#1C2227]">
      <div className="flex w-full shrink-0 items-center justify-between bg-gray-300 p-4 dark:bg-[#1F2C36]">
        <h3 className="text-[15px] font-bold text-paragraph dark:text-white">
          Recent Transactions
        </h3>
        <TransactionHistoryRefreshButton
          onRefresh={handleRefresh}
          isRefreshing={isFetching && !isLoading}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <ClipLoader color="#5E91FF" size={40} />
          </div>
        ) : transactions.length >= 1 ? (
          <div className="w-full">
            {transactions.map((transaction) => (
              <div
                key={transaction._id}
                onClick={() => setSelectedTransaction(transaction)}
                className="mt-2 flex cursor-pointer items-center justify-between rounded-xl border-b border-gray-100 bg-white px-4 py-5 dark:border-gray-800 dark:bg-[#1C2227]"
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-[24px] w-[24px] items-center justify-center rounded-full">
                    {getTransactionStatusLabel(toStatusInput(transaction)) ===
                    "PENDING" ? (
                      <img
                        src={pendingClock}
                        className="h-full w-full bg-cover"
                        alt=""
                      />
                    ) : (
                      <img
                        src={nairaLogo}
                        className="h-full w-full bg-cover"
                        alt=""
                      />
                    )}
                  </div>
                  <div>
                    <h4 className="text-[13px] font-semibold capitalize text-[#13181B] dark:text-gray-200">
                      {transaction.narration ||
                        formatTxnTypeLabel(transaction.type)}
                    </h4>
                    <div className="flex items-center gap-3">
                      <h4 className="text-[11px] font-medium text-gray-500">
                        {formatDateWithSuffix(transaction.createdAt)}
                      </h4>
                      <div className="h-[4px] w-[4px] bg-[#D9D9D9]" />
                      <h4
                        className={getTransactionStatusClass(
                          toStatusInput(transaction)
                        )}
                      >
                        {getTransactionStatusLabel(toStatusInput(transaction))}
                      </h4>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-right text-[13px] font-semibold text-[#13181B] dark:text-gray-300">
                    ₦{formatAmount(transaction.amount)}
                  </h4>
                  {transaction.fee > 0 && (
                    <h4 className="text-right text-[11px] text-gray-500 dark:text-gray-400">
                      Fee: ₦{formatAmount(transaction.fee)}
                    </h4>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center">
            {theme === "dark" ? (
              <img src={darkNoTransaction} alt="No Transaction" />
            ) : (
              <img src={noTransaction} alt="No Transaction" />
            )}
            <h4 className="pt-2 font-bold text-[#869AA9]">
              No NGN transaction
            </h4>
            <p className="pt-2 text-[12px] text-[#869AA9]">
              All your NGN transactions will show up here
            </p>
          </div>
        )}
      </div>

      {transactionsCount > 0 && (
        <div className="mt-auto flex shrink-0 items-center justify-center gap-4 border-t border-gray-100 bg-white py-3 dark:border-gray-800 dark:bg-[#1C2227]">
          <button
            type="button"
            onClick={handlePrevPage}
            disabled={page === 1 || isLoading}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 font-sora text-[12px] font-medium transition-all duration-200 ${
              page === 1 || isLoading
                ? "cursor-not-allowed text-gray-400"
                : "font-semibold text-secondary_button hover:bg-secondary_button hover:text-white"
            }`}
          >
            <BsChevronLeft className="text-xs" />
            Previous
          </button>

          <div className="flex items-center gap-2 font-sora text-[12px] font-semibold">
            <span className="text-gray-600 dark:text-gray-400">
              Page {pagination?.page ?? page} of {totalPages}
            </span>
            {isLoading && <ClipLoader color="#5E91FF" size={16} />}
          </div>

          <button
            type="button"
            onClick={handleNextPage}
            disabled={!hasMore || isLoading}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 font-sora text-[12px] transition-all duration-200 ${
              !hasMore || isLoading
                ? "cursor-not-allowed text-gray-400"
                : "font-semibold text-secondary_button hover:bg-secondary_button hover:text-white"
            }`}
          >
            Next
            <BsChevronRight className="text-xs" />
          </button>
        </div>
      )}

      {selectedTransaction && (
        <div className="fixed inset-0 z-[140] flex items-start justify-center overflow-auto bg-black/75 pb-16 pt-16 font-manrope">
          <div className="relative mx-auto w-11/12 max-w-md rounded-xl border bg-white p-6 dark:border-[#303030] dark:bg-[#192025]">
            <button
              type="button"
              onClick={() => setSelectedTransaction(null)}
              className="absolute right-4 top-4 text-gray-500"
              aria-label="Close"
            >
              <IoClose className="text-xl" />
            </button>

            <h4 className="font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
              Transaction details
            </h4>

            <div className="mt-6 space-y-4">
              <div className="border-b border-gray-200 py-3 dark:border-gray-600">
                <p className="text-[13px] font-bold text-secondary_button">
                  Type
                </p>
                <p className="mt-1 text-[16px] text-gray-900 dark:text-gray-100">
                  {formatTxnTypeLabel(selectedTransaction.type)}
                </p>
              </div>
              <div className="border-b border-gray-200 py-3 dark:border-gray-600">
                <p className="text-[13px] font-bold text-secondary_button">
                  Amount
                </p>
                <p className="mt-1 font-sora text-[20px] font-bold text-gray-900 dark:text-gray-100">
                  ₦{formatAmount(selectedTransaction.amount)}
                </p>
              </div>
              {selectedTransaction.fee > 0 && (
                <div className="border-b border-gray-200 py-3 dark:border-gray-600">
                  <p className="text-[13px] font-bold text-secondary_button">
                    Fee
                  </p>
                  <p className="mt-1 text-[16px] text-gray-900 dark:text-gray-100">
                    ₦{formatAmount(selectedTransaction.fee)}
                  </p>
                </div>
              )}
              <div className="border-b border-gray-200 py-3 dark:border-gray-600">
                <p className="text-[13px] font-bold text-secondary_button">
                  Status
                </p>
                <p
                  className={`mt-1 text-[16px] font-semibold ${getTransactionStatusClass(
                    toStatusInput(selectedTransaction)
                  )}`}
                >
                  {getTransactionStatusLabel(
                    toStatusInput(selectedTransaction)
                  )}
                </p>
              </div>
              {selectedTransaction.narration && (
                <div className="border-b border-gray-200 py-3 dark:border-gray-600">
                  <p className="text-[13px] font-bold text-secondary_button">
                    Narration
                  </p>
                  <p className="mt-1 text-[16px] text-gray-900 dark:text-gray-100">
                    {selectedTransaction.narration}
                  </p>
                </div>
              )}
              <div className="border-b border-gray-200 py-3 dark:border-gray-600">
                <p className="text-[13px] font-bold text-secondary_button">
                  Date
                </p>
                <p className="mt-1 text-[16px] text-gray-900 dark:text-gray-100">
                  {formatDateWithSuffix(selectedTransaction.createdAt)}
                </p>
              </div>
              {selectedTransaction.counterParty && (
                <div className="py-3">
                  <p className="text-[13px] font-bold text-secondary_button">
                    Counter party
                  </p>
                  {selectedTransaction.counterParty.accountName && (
                    <p className="mt-1 text-[16px] text-gray-900 dark:text-gray-100">
                      {selectedTransaction.counterParty.accountName}
                    </p>
                  )}
                  {selectedTransaction.counterParty.accountNumber && (
                    <p className="mt-1 text-[14px] text-[#BE6B0A]">
                      {selectedTransaction.counterParty.accountNumber}
                      {selectedTransaction.counterParty.bankName
                        ? ` - ${selectedTransaction.counterParty.bankName}`
                        : ""}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NairaRecentTnxn;
